import Route from '@ember/routing/route'
import { service } from '@ember/service'
import {
  PRINTABLES_TABS,
  PRINTABLES_TAB_READING,
  PRINTABLES_TAB_MATHS,
  PRINTABLES_TAB_WRITING,
  PRINTABLES_SUB_TYPE_READING,
  PRINTABLES_SUB_TYPE_MATHS,
  PRINTABLES_SUB_TYPE_WRITING,
} from 'teacher-ui-client/subscription-type/printables/tabs'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { PartnerCode } from 'teacher-ui-client/partner/partners'
import type ContentfulClientService from 'teacher-ui-client/services/contentful-client'
import type SessionService from 'teacher-ui-client/services/session'
import type PartnerService from 'teacher-ui-client/services/partner'
import type { PrintablesQuickFiltersEntry } from 'teacher-ui-client/contentful-client/entry-types'
import type { IntlService } from 'ember-intl'
import { Product } from 'teacher-ui-client/utils/products'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'

interface Params extends Record<string, unknown> {
  tab: string
}

export default class PrintablesRoute extends Route {
  @service contentfulClient!: ContentfulClientService

  @service session!: SessionService

  @service partner!: PartnerService

  @service intl!: IntlService

  @service featureControl!: FeatureControlService

  queryParams = { tab: { refreshModel: true } }

  async model(params: Params) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute

    const { tab: selectedTabId } = params

    const tabs = this.tabs

    const selectedTab = tabs.find((printablesTab) => printablesTab.id === selectedTabId)

    // Set the default tab based on subscription type. If that tab isn't available, use the first one.
    let defaultTabId = PRINTABLES_TAB_READING
    switch (subscriptionType) {
      case SubscriptionType.Reading:
        defaultTabId = PRINTABLES_TAB_READING
        break
      case SubscriptionType.Maths:
        defaultTabId = PRINTABLES_TAB_MATHS
        break
      case SubscriptionType.Writing:
        defaultTabId = PRINTABLES_TAB_WRITING
        break
    }
    const defaultTab = tabs.find((printablesTab) => printablesTab.id === defaultTabId) ?? tabs[0]

    const currentTab = selectedTab ?? defaultTab

    const [quickFiltersForAllTabs, bookSets] = await Promise.all([
      this.contentfulClient.getPrintablesQuickFilters(),
      this.contentfulClient.getBookSets(currentTab),
    ])

    const currentTabId = currentTab.id
    const filters = this.getFiltersForTab(quickFiltersForAllTabs, currentTab.quickFilterId).map((filter) => {
      const [field, value] = filter.split('=')
      if (field === 'fields.product' && value) {
        return {
          id: filter,
          label: this.intl.t(`products.${value}`),
        }
      } else {
        return {
          id: filter,
          label: filter,
        }
      }
    })

    return {
      subscriptionType,
      bookSets: this.featureControl.canAccess('msp_product')
        ? bookSets
        : bookSets.filter((bookSet) => bookSet.fields.product !== Product.MSP),
      currentTabId,
      filters,
      tabs,
    }
  }

  get showWritingTab() {
    return this.featureControl.canAccess('writing_area')
  }

  get tabs() {
    let tabs = PRINTABLES_TABS

    /* RS partner logic
     * When school has RS partner for reading, only keep reading tabs if they have an active reading school sub too.
     * When school has RS partner for maths, only keep maths tabs if they have an active maths school sub too.
     *
     * This page shouldn't be accessible by that user if they are missing an active school sub for both.
     */
    if (this.hasRatnasagarPartnerForReading && !this.hasActiveReadingSchoolSub) {
      tabs = tabs.filter((printablesTab) => printablesTab.subscriptionType !== PRINTABLES_SUB_TYPE_READING)
    }
    if (this.hasRatnasagarPartnerForMaths && !this.hasActiveMathsSchoolSub) {
      tabs = tabs.filter((printablesTab) => printablesTab.subscriptionType !== PRINTABLES_SUB_TYPE_MATHS)
    }
    // TODO when releasing Writing to India, consider applying specific Ratnasagar printables logic above.

    /* Filter out writing tab if not enabled */
    if (!this.showWritingTab) {
      tabs = tabs.filter((printablesTab) => printablesTab.subscriptionType !== PRINTABLES_SUB_TYPE_WRITING)
    }
    return tabs
  }

  private getFiltersForTab(quickFilterSets: PrintablesQuickFiltersEntry[], quickFilterId: string) {
    const printablesTabLowerCase = quickFilterId.toLowerCase()
    return (
      quickFilterSets.find((filterSet) => {
        return printablesTabLowerCase === filterSet.fields.displayTab?.toLowerCase()
      })?.fields?.keywords ?? []
    )
  }

  get hasRatnasagarPartnerForReading() {
    return this.partner.hasPartner(PartnerCode.RatnaSagar, SubscriptionType.Reading)
  }

  get hasRatnasagarPartnerForMaths() {
    return this.partner.hasPartner(PartnerCode.RatnaSagar, SubscriptionType.Maths)
  }

  get hasActiveReadingSchoolSub() {
    return Boolean(
      this.session.currentSchool.schoolSubscriptions?.find(
        (schoolSub) => schoolSub.subscriptionType === SubscriptionType.Reading && !schoolSub.isExpired,
      ),
    )
  }

  get hasActiveMathsSchoolSub() {
    return Boolean(
      this.session.currentSchool.schoolSubscriptions?.find(
        (schoolSub) => schoolSub.subscriptionType === SubscriptionType.Maths && !schoolSub.isExpired,
      ),
    )
  }
}
